import { useAppStore } from '@/state-management';
import type { AlertProps } from '@mui/material';
import { Alert } from '@mui/material';
import { useRef } from 'react';
import type { MedicationPadItemCancelData, MedicationPadItemType } from '../../PrescriptionPad';
import { isMedicationOutOfService } from '../../PrescriptionPad';
import { PRODUCT_ALERT_MESSAGE_TEMPLATE_REISSUE } from '../../ProductDetailsCard/ProductDetailsCard.constants';
import { PrescriptionItemAlertType } from '../PrescriptionItemEditor.types';
import { useAlertType, useCancelAlert } from '../hooks';

type AlertMessage = {
  severity: AlertProps['severity'];
  ariaLabel: string;
  description: React.ReactNode;
};

export type PrescriptionItemAlertProps = {
  medicationItemId: string;
  productId: number;
};

export const PrescriptionItemAlert = ({ medicationItemId }: PrescriptionItemAlertProps) => {
  const {
    prescriptionPad: { medicationPadItems },
    prescriptionHistory: { itemsToReissue, itemsToCancel }
  } = useAppStore.use.prescriptionAssistant();

  const reissuingMedication = itemsToReissue.find((item) => item.medicationPadItemId === medicationItemId);
  const currentMedicationPadItem = medicationPadItems.find(
    (existingItem) => existingItem.medicationPadItemId === medicationItemId
  ) ?? {
    medicationPadItemId: medicationItemId
  };

  const { cancelAlertData } = useCancelAlert({
    initialMedication: reissuingMedication,
    currentMedicationPadItem: currentMedicationPadItem?.medicationPadItem,
    itemsToCancel
  });

  const activeAlertType = useAlertType({
    reissueStatus: currentMedicationPadItem?.reissueStatus,
    errorMessage: currentMedicationPadItem?.errorMessage ?? null,
    hasActiveCancelAlert: !!cancelAlertData
  });

  const initialMedicationRef = useRef(reissuingMedication);

  const isOutOfService =
    initialMedicationRef?.current?.reissueStatus &&
    isMedicationOutOfService(initialMedicationRef.current.reissueStatus);

  const getAlertMessage = (
    alertType: PrescriptionItemAlertType | null,
    errorMessage: { title: string; description: string } | null,
    cancelAlertData: MedicationPadItemCancelData | null,
    currentMedicationPadItem: MedicationPadItemType | null
  ): AlertMessage | null => {
    if (!alertType) {
      return null;
    }

    const inactiveAlertDescription = (
      <>
        This product is currently <strong>unavailable for ordering</strong>. Please select an alternative to proceed.
      </>
    );

    const alertConfig: Record<PrescriptionItemAlertType, AlertMessage> = {
      [PrescriptionItemAlertType.ERROR]: {
        severity: 'error',
        ariaLabel: 'Error message',
        description: errorMessage && (
          <>
            <strong>{errorMessage.title} </strong>
            {errorMessage.description}
          </>
        )
      },
      [PrescriptionItemAlertType.CANCEL]: {
        severity: isOutOfService ? 'info' : 'warning',
        ariaLabel: `${isOutOfService ? 'Out of Service' : 'Reissued'} Medication cancelled`,
        description:
          cancelAlertData &&
          (isOutOfService ? (
            <>
              <strong>{currentMedicationPadItem?.medicationPadItem?.productName ?? 'A new product'}</strong> will be
              issued as an alternative to {cancelAlertData.medicationName}
            </>
          ) : (
            <>
              <strong>
                {[cancelAlertData.supplierName, cancelAlertData.medicationName].filter(Boolean).join(' | ')}
              </strong>{' '}
              will be cancelled and updated to the medication selected.
            </>
          ))
      },
      [PrescriptionItemAlertType.DISCONTINUED]: {
        severity: 'error',
        ariaLabel: 'Discontinued medication',
        description: (
          <>
            This product has been <strong>discontinued</strong> and will be cancelled. Please select an alternative to
            proceed.
          </>
        )
      },
      [PrescriptionItemAlertType.OUT_OF_STOCK]: {
        severity: 'error',
        ariaLabel: 'Out of stock medication',
        description: (
          <>
            This product is currently <strong>out of stock</strong>. Please select an alternative to proceed.
          </>
        )
      },
      [PrescriptionItemAlertType.INACTIVE]: {
        severity: 'error',
        ariaLabel: 'Inactive medication',
        description: inactiveAlertDescription
      },
      [PrescriptionItemAlertType.UNAVAILABLE]: {
        severity: 'error',
        ariaLabel: 'Unavailable medication',
        description: inactiveAlertDescription
      },
      [PrescriptionItemAlertType.REISSUE]: {
        severity: 'info',
        ariaLabel: 'Re-issued medication',
        description: (
          <>
            <strong>
              {currentMedicationPadItem?.medicationPadItem?.supplierName &&
              currentMedicationPadItem?.medicationPadItem?.productName
                ? `${currentMedicationPadItem?.medicationPadItem?.supplierName} | ${currentMedicationPadItem?.medicationPadItem?.productName}`
                : currentMedicationPadItem?.medicationPadItem?.productName || 'The product'}
            </strong>{' '}
            {PRODUCT_ALERT_MESSAGE_TEMPLATE_REISSUE}.
          </>
        )
      }
    };

    return alertConfig[alertType];
  };

  const alertMessage = getAlertMessage(
    activeAlertType,
    currentMedicationPadItem?.errorMessage ?? null,
    cancelAlertData,
    currentMedicationPadItem ?? null
  );

  if (!alertMessage) {
    return null;
  }

  return (
    <Alert aria-label={alertMessage.ariaLabel} severity={alertMessage.severity}>
      {alertMessage.description}
    </Alert>
  );
};
